import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useUserState } from '../../lib/userState';

// SA regions. Compact chip list (circular thumb + name + short tag) so the
// section guides without dominating the page. Each links to its region page.
const SA_REGIONS = [
  { name: 'Northern Adelaide', tag: 'Growth corridor', href: '/home-builders-northern-adelaide', image: '/images/regions/thumbs/northern-adelaide.webp' },
  { name: 'Southern Adelaide', tag: 'Coastal & bay', href: '/home-builders-southern-adelaide', image: '/images/regions/thumbs/southern-adelaide.webp' },
  { name: 'Adelaide Hills', tag: 'Acreage & slopes', href: '/home-builders-adelaide-hills', image: '/images/regions/thumbs/adelaide-hills.webp' },
  { name: 'Western Adelaide', tag: 'Infill & KDR', href: '/home-builders-western-adelaide', image: '/images/regions/thumbs/western-adelaide.webp' },
  { name: 'Eastern Adelaide', tag: 'Premium established', href: '/south-australia/adelaide/areas', image: '/images/regions/thumbs/eastern-adelaide.webp' },
  { name: 'Inner Adelaide', tag: 'City fringe', href: '/south-australia/adelaide/areas', image: '/images/regions/thumbs/inner-adelaide.webp' },
  { name: 'Mount Barker & Hills', tag: 'Fastest-growing', href: '/building-in/mount-barker', image: '/images/regions/thumbs/mount-barker.webp' },
  { name: 'Country SA', tag: 'Rural & acreage', href: '/country-home-builders-south-australia', image: '/images/regions/thumbs/country-sa.webp' },
];

// Victoria regions. All link into the Victoria hub for now (per-region VIC
// pages not yet built).
const VIC_REGIONS = [
  { name: 'Inner Melbourne', tag: 'City fringe', href: '/vic', image: '/images/regions-vic/thumbs/inner-melbourne.webp' },
  { name: 'Northern Melbourne', tag: 'Growth corridors', href: '/vic', image: '/images/regions-vic/thumbs/northern-melbourne.webp' },
  { name: 'Western Growth', tag: 'New estates', href: '/vic', image: '/images/regions-vic/thumbs/western-growth.webp' },
  { name: 'Eastern Melbourne', tag: 'Established', href: '/vic', image: '/images/regions-vic/thumbs/eastern-melbourne.webp' },
  { name: 'South-East Melbourne', tag: 'Family-home belt', href: '/vic', image: '/images/regions-vic/thumbs/south-east-melbourne.webp' },
  { name: 'Mornington Peninsula', tag: 'Coastal', href: '/vic', image: '/images/regions-vic/thumbs/mornington-peninsula.webp' },
  { name: 'Yarra Ranges & Hills', tag: 'Sloping & treed', href: '/vic', image: '/images/regions-vic/thumbs/yarra-ranges.webp' },
  { name: 'Regional Victoria', tag: 'Regional centres', href: '/vic', image: '/images/regions-vic/thumbs/regional-victoria.webp' },
];

const RegionGrid = () => {
  const userState = useUserState();
  const isVic = userState === 'VIC';
  const isSa = userState === 'SA';
  // Generic (outside SA/VIC, or no state picked yet): show both live markets
  // side-by-side rather than defaulting to Adelaide.
  const GENERIC_REGIONS = [...SA_REGIONS.slice(0, 4), ...VIC_REGIONS.slice(0, 4)];
  const regions = isVic ? VIC_REGIONS : isSa ? SA_REGIONS : GENERIC_REGIONS;
  const headline = isVic ? 'Victoria' : isSa ? 'South Australia' : 'Adelaide & Melbourne';
  const headingText = isVic || isSa ? (<>Pick your <span className="gradient-text-orange">{headline}</span> region</>) : (<>Explore <span className="gradient-text-orange">{headline}</span></>);
  const subtitle = isVic || isSa ? 'Local builders, blocks and costs by area.' : 'BuildPilot is live across Adelaide and Melbourne - explore builders, blocks and costs by area.';
  const seeAllHref = isVic ? '/vic' : isSa ? '/suburbs' : '/suburbs';
  const seeAllLabel = isVic ? 'Explore the Victoria hub' : isSa ? 'See all suburbs' : 'See all areas';

  return (
    <section className="py-9 sm:py-12 bg-white" data-testid="region-grid-section" data-region-state={userState || 'generic'}>
      <div className="max-w-[1120px] mx-auto px-5 sm:px-6">
        <div className="flex flex-col items-center text-center gap-2 mb-5 sm:flex-row sm:items-end sm:justify-between sm:text-left">
          <div>
            <h2 className="text-[18px] sm:text-[22px] font-bold text-[#1d1d1f] tracking-tight leading-tight">
              {headingText}
            </h2>
            <p className="text-[13px] text-[#86868b] mt-1">
              {subtitle}
            </p>
          </div>
          <Link
            to={seeAllHref}
            className="shrink-0 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#1d8efe] hover:text-[#0c6fd6] transition-colors"
            data-testid="region-grid-see-all"
          >
            {seeAllLabel} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div className="flex flex-wrap gap-2 sm:gap-2.5" data-testid="region-grid">
          {regions.map((r) => (
            <Link
              key={r.name}
              to={r.href}
              className="group inline-flex items-center gap-2.5 pl-1.5 pr-3.5 py-1.5 rounded-full bg-white border border-gray-200 hover:border-[#1d8efe] hover:shadow-sm transition-all"
              data-testid={`region-card-${r.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              <img
                src={r.image}
                alt=""
                aria-hidden="true"
                loading="lazy"
                decoding="async"
                width="32"
                height="32"
                className="w-8 h-8 rounded-full object-cover shrink-0"
              />
              <span className="text-[13px] font-semibold text-[#1d1d1f] group-hover:text-[#1d8efe] transition-colors whitespace-nowrap">
                {r.name}
              </span>
              <span className="text-[12px] text-[#a1a1a6] whitespace-nowrap">{r.tag}</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RegionGrid;
